import { useEffect, useMemo, useState } from "react";
import { Tag as TagIcon } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { type PointCloudTag, updateTag } from "../../lib/pointCloudsApi";
import api from "../../lib/api";

interface AssetOption {
  id: string;
  name: string;
  asset_code: string;
}

interface EditTagModalProps {
  isOpen: boolean;
  onClose: () => void;
  tag: PointCloudTag | null;
  virtualSiteId: string;
  onSaved: () => void;
  taggedAssetIds?: ReadonlyArray<string>;
}

export function EditTagModal({
  isOpen, onClose, tag, virtualSiteId, onSaved, taggedAssetIds = [],
}: EditTagModalProps) {
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [assetId, setAssetId] = useState("");
  const [label, setLabel] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !tag) return;
    setAssetId(tag.asset_id);
    setLabel(tag.label ?? "");
    setColor(tag.color || "#6366f1");
    setError(null);
  }, [isOpen, tag]);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setIsLoadingAssets(true);
    api
      .get("/assets", { params: { virtual_site_id: virtualSiteId, page_size: 100 } })
      .then((res) => {
        if (!cancelled) setAssets(res.data.items || []);
      })
      .catch(() => {
        if (!cancelled) setAssets([]);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAssets(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, virtualSiteId]);

  const availableAssets = useMemo(() => {
    const taken = new Set(taggedAssetIds);
    if (tag?.asset_id) taken.delete(tag.asset_id);
    return assets.filter((a) => !taken.has(a.id));
  }, [assets, taggedAssetIds, tag?.asset_id]);

  function handleClose() {
    if (isSaving) return;
    onClose();
  }

  async function handleSave() {
    if (!tag) return;
    setIsSaving(true);
    setError(null);
    try {
      const changedAsset = assetId && assetId !== tag.asset_id ? assetId : undefined;
      const changedLabel = label !== (tag.label ?? "") ? label : undefined;
      const changedColor = color !== (tag.color || "#6366f1") ? color : undefined;
      if (!changedAsset && changedLabel === undefined && !changedColor) {
        onClose();
        return;
      }
      await updateTag(tag.id, {
        asset_id: changedAsset,
        label: changedLabel,
        color: changedColor,
      });
      onSaved();
      onClose();
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to update tag.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Tag" size="md">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        {tag && (
          <div className="rounded-lg border border-surface-700 bg-surface-900/50 px-4 py-3 text-xs text-surface-400">
            <div className="flex items-center gap-2">
              <TagIcon className="h-3.5 w-3.5 text-brand-400" />
              <span className="font-medium text-surface-200">3D Position</span>
            </div>
            <div className="mt-1 font-mono">
              {(() => {
                const g = (tag.geometry || {}) as Record<string, unknown>;
                const x = g.x as number;
                const y = g.y as number;
                const z = g.z as number;
                return typeof x === "number" && typeof y === "number" && typeof z === "number"
                  ? `x: ${x.toFixed(3)} · y: ${y.toFixed(3)} · z: ${z.toFixed(3)}`
                  : "(non-point geometry)";
              })()}
            </div>
            <p className="mt-2 text-[11px] text-surface-500">
              Position is fixed. To re-place, delete this tag and click on the cloud again.
            </p>
          </div>
        )}

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">
            Asset <span className="text-red-400">*</span>
          </label>
          <select
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
            disabled={isLoadingAssets || isSaving}
            className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          >
            {isLoadingAssets && <option value="">Loading assets…</option>}
            {availableAssets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.asset_code} — {a.name}
              </option>
            ))}
          </select>
          {taggedAssetIds.length > 1 && (
            <p className="mt-1 text-[11px] text-surface-500">
              Assets already tagged on this point cloud are hidden (except the current one).
            </p>
          )}
        </div>

        <Input
          label="Label"
          placeholder="Optional, e.g. valve handwheel"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={isSaving}
        />

        <div>
          <label className="mb-1.5 block text-sm font-medium text-surface-200">Color</label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            disabled={isSaving}
            className="h-10 w-20 rounded-lg border border-surface-700 bg-surface-900"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!assetId}>
            Save changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}
